/**
 * Runtime flag for the consolidated "Confirm your filing" flow (Walter
 * R49/R64–R67, phases 1+).
 *
 * The merged four-step flow (Upload → Confirm your filing → Review Fees →
 * Submit) is the DEFAULT, and the URL ALONE decides, per page load (Walter
 * EARS-13 REQ-13.1). `?confirmFlow=0` selects the classic five-step flow for
 * that load only; `?confirmFlow=1` selects the confirm flow; any URL without a
 * `confirmFlow` param — including a plain reload or an in-app navigation away
 * from a `?confirmFlow=0` page — resolves to the confirm-flow default.
 *
 * Nothing is persisted. Through 2026-08-24 the choice was written to
 * `localStorage['efilingConfirmFlow']` so a demo browser stayed classic across
 * navigations (PR #931); that made a bare URL's flow depend on invisible
 * per-browser state, so a tester running both flows in one session could
 * attribute a result to the wrong flow. A stale `efilingConfirmFlow` key left
 * by that scheme is simply IGNORED — this module reads no storage at all, so
 * such a key can no longer influence any page load.
 *
 * Because the resolution is per-load, a caller wanting a non-default flow must
 * carry the param on the navigation itself (the `pinClassicFlow` /
 * `pinConfirmFlow` E2E fixtures do exactly that).
 */

/**
 * Resolve the flag from the URL query string alone. Defaults to the confirm
 * flow whenever `confirmFlow` is absent or holds anything other than an
 * explicit off value. Pure: reads no storage and writes nothing.
 */
export function resolveConfirmFlowFlag(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  const param = new URLSearchParams(search).get('confirmFlow');
  return !(param === '0' || param === 'false');
}
